import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Moment from 'moment';
import 'moment/locale/es';
import {
  Box,
  Flex,

  Heading,
  Text,
  Badge,
  Avatar,
  IconButton,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  HStack,
  VStack,
  SimpleGrid,
  Icon,
  Tag,
  TagLabel,
  Tooltip
} from '@chakra-ui/react';
import {
  FaArrowLeft,
  FaUser,
  FaIdCard,
  FaMapMarkerAlt,
  FaBirthdayCake,
  FaPhoneAlt,
  FaEnvelope,
  FaHome,
  FaUsers,
  FaUserMd,
  FaInfoCircle,
  FaBook,
  FaChurch,
  FaHeartbeat,
  FaHandsHelping,
  FaUserCheck
} from 'react-icons/fa';

import { Loading } from '../../helpers/Loading';
import { getEstudiante, reset } from '../../features/estudianteSlice';

// Función auxiliar para mostrar campos vacíos
const DisplayField = ({ label, value, icon }) => {
  const emptyValue = value === undefined || value === null || value === '';

  return (
    <Flex
      p={3}
      borderRadius="md"
      bg={useColorModeValue('gray.50', 'gray.700')}
      alignItems="center"
      mb={2}
    >
      {icon && (
        <Icon as={icon} mr={3} color={'blue.400'} />
      )}
      <Box>
        <Text fontSize="sm" fontWeight="medium" color={'gray.500'}>
          {label}
        </Text>
        <Text fontWeight="semibold">
          {emptyValue ? 'Sin registro' : value}
        </Text>
      </Box>
    </Flex>
  );
};

// Función auxiliar para mostrar etiquetas booleanas
const BooleanTag = ({ value, label, colorTrue = "green", colorFalse = "gray" }) => (
  <Tag
    size="md"
    colorScheme={value ? colorTrue : colorFalse}
    borderRadius="full"
    px={3}
    py={1}
  >
    <TagLabel>{value ? `${label}` : `No ${label}`}</TagLabel>
  </Tag>
);

const DetallesEstudiante = () => {
  // Configurar el idioma de Moment a español
  Moment.locale('es');

  const dispatch = useDispatch();
  const { sedeSeleccionada } = useSelector(state => state.auth);
  const { estudiante, isLoading } = useSelector(state => state.estudiantes);
  const params = useParams();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    dispatch(getEstudiante(params.id));

    return () => {
      dispatch(reset());
    };
  }, [dispatch, params.id]);

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return "No disponible";

    const hoy = new Date();
    const fechaNac = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - fechaNac.getFullYear();
    const mes = hoy.getMonth() - fechaNac.getMonth();

    if (mes < 0 || (mes === 0 && hoy.getDate() < fechaNac.getDate())) {
      edad--;
    }

    return `${edad} años`;
  };

  const getBithdayTimer = birthday => {
    if (!birthday) return 'No hay fecha de nacimiento';

    const birthdayDate = new Date(birthday);
    const today = new Date();

    // Establecer el año de la fecha de cumpleaños al año actual
    birthdayDate.setFullYear(today.getFullYear());

    // Si el cumpleaños ya pasó este año, ajustar al próximo año
    if (birthdayDate < today) {
      birthdayDate.setFullYear(today.getFullYear() + 1);
    }

    // Calcular diferencia en días
    const diffTime = birthdayDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return '¡Hoy es su cumpleaños!';
    } else if (diffDays === 1) {
      return 'Mañana es su cumpleaños';
    } else if (diffDays <= 30) {
      return `Faltan ${diffDays} días`;
    } else {
      const months = Math.floor(diffDays / 30);
      const remainingDays = diffDays % 30;
      return `Faltan ${months} ${months === 1 ? 'mes' : 'meses'} y ${remainingDays} días`;
    }
  };

  const getEstadoBadgeColor = (estado) => {
    switch (estado) {
      case 'ACTIVO':
        return 'green';
      case 'RETIRADO':
        return 'gray';
      case 'SUSPENDIDO':
        return 'orange';
      default:
        return 'red';
    }
  };

  const getTipoDocumentoText = (tipo) => {
    switch (tipo) {
      case 'RC':
        return 'Registro Civil';
      case 'TI':
        return 'Tarjeta de Identidad';
      case 'CC':
        return 'Cédula de Ciudadanía';
      default:
        return tipo;
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  const faltaCumpleanios = getBithdayTimer(estudiante?.fecha_nacimiento);

  return (
    <Box mx="auto" px={4}>
      <Flex justifyContent="space-between" alignItems="center" py={2}>
        <HStack spacing={4}>
          <Link to={`/${sedeSeleccionada?._id}/estudiantes`}>
            <IconButton
              icon={<FaArrowLeft />}
              colorScheme="blue"
              rounded="full"
              size="md"
              aria-label="Regresar"
            />
          </Link>
          <Text fontSize="md" fontWeight="medium">Regresar a la lista</Text>
        </HStack>
        <Heading size="md">Detalles del Estudiante</Heading>
      </Flex>

      {/* Panel de información básica */}
      <Box
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="xl"
        overflow="hidden"
        bg={bgColor}
        shadow="md"
        mb={6}
      >
        <Flex
          direction={{ base: "column", md: "row" }}
          p={{ base: 4, md: 6 }}
          alignItems={{ base: "center", md: "flex-start" }}
        >
          {/* Avatar y estado */}
          <VStack spacing={4} alignItems="center" minW={{ base: "100%", md: "250px" }} mb={{ base: 6, md: 0 }}>
            <Avatar
              size="2xl"
              name={`${estudiante?.nombres} ${estudiante?.apellidos}`}
              src={estudiante?.img}
              bg="blue.500"
              color="white"
              fontWeight="bold"
              fontSize="2xl"
              p={1}
              border="4px solid"
              borderColor={'gray.300'}
            />

            <Badge
              colorScheme={getEstadoBadgeColor(estudiante?.estado)}
              variant="solid"
              fontSize="md"
              px={4}
              py={2}
              borderRadius="full"
            >
              {estudiante?.estado || 'ACTIVO'}
            </Badge>

            {estudiante?.fecha_nacimiento && (
              <Tooltip label={Moment(estudiante?.fecha_nacimiento).format('LL')} placement="top">
                <Badge
                  colorScheme="purple"
                  variant="solid"
                  px={4}
                  py={2}
                  borderRadius="full"
                >
                  {faltaCumpleanios}
                </Badge>
              </Tooltip>
            )}
          </VStack>

          {/* Información básica */}
          <Box flex="1" pl={{ base: 0, md: 6 }}>
            <Heading size="md" mb={4} textAlign={{ base: "center", md: "left" }}>
              {estudiante?.nombres} {estudiante?.apellidos}
            </Heading>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <DisplayField
                label="Tipo de documento"
                value={getTipoDocumentoText(estudiante?.tipo_documento)}
                icon={FaIdCard}
              />

              <DisplayField
                label="Número de documento"
                value={estudiante?.dni}
                icon={FaIdCard}
              />

              <DisplayField
                label="Lugar de expedición"
                value={estudiante?.lugar_expedicion}
                icon={FaMapMarkerAlt}
              />

              <DisplayField
                label="Fecha de nacimiento"
                value={estudiante?.fecha_nacimiento
                  ? Moment(estudiante?.fecha_nacimiento).format('LL')
                  : null}
                icon={FaBirthdayCake}
              />

              <DisplayField
                label="Edad"
                value={estudiante?.edad || calcularEdad(estudiante?.fecha_nacimiento)}
                icon={FaUser}
              />

              <DisplayField
                label="Género"
                value={estudiante?.sexo === 'M' ? 'MASCULINO' : 'FEMENINO'}
                icon={FaUser}
              />

              <DisplayField
                label="Teléfono"
                value={estudiante?.celular}
                icon={FaPhoneAlt}
              />

              <DisplayField
                label="Correo electrónico"
                value={estudiante?.correo}
                icon={FaEnvelope}
              />
            </SimpleGrid>
          </Box>
        </Flex>
      </Box>

      {/* Pestañas para información detallada */}
      <Tabs variant="enclosed" colorScheme="blue" borderRadius="xl" shadow="md">
        <TabList overflowX="auto" overflowY="hidden" whiteSpace="nowrap">
          <Tab><Icon as={FaMapMarkerAlt} mr={2} /> Información Geográfica</Tab>
          <Tab><Icon as={FaUsers} mr={2} /> Información Familiar</Tab>
          <Tab><Icon as={FaHeartbeat} mr={2} /> Información de Salud</Tab>
          <Tab><Icon as={FaHandsHelping} mr={2} /> Información Social</Tab>
          <Tab><Icon as={FaInfoCircle} mr={2} /> Información Adicional</Tab>
        </TabList>

        <TabPanels>
          {/* Información Geográfica */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <DisplayField
                label="Dirección de residencia"
                value={estudiante?.direccion_residencia}
                icon={FaHome}
              />

              <DisplayField
                label="Vereda"
                value={estudiante?.vereda}
                icon={FaMapMarkerAlt}
              />

              <DisplayField
                label="Municipio de nacimiento"
                value={estudiante?.municipio_nacimiento}
                icon={FaMapMarkerAlt}
              />

              <DisplayField
                label="Departamento de nacimiento"
                value={estudiante?.departamento_nacimiento}
                icon={FaMapMarkerAlt}
              />

              <DisplayField
                label="Nacionalidad"
                value={estudiante?.nacionalidad}
                icon={FaMapMarkerAlt}
              />

              <DisplayField
                label="Estrato"
                value={estudiante?.estrato}
                icon={FaHome}
              />
            </SimpleGrid>
          </TabPanel>

          {/* Información Familiar */}
          <TabPanel>
            <Box mb={6}>
              <Heading size="sm" mb={4} color="blue.600">Padre</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <DisplayField
                  label="Nombre completo"
                  value={estudiante?.padre?.nombre}
                  icon={FaUser}
                />

                <DisplayField
                  label="Número de identificación"
                  value={estudiante?.padre?.cc}
                  icon={FaIdCard}
                />

                <DisplayField
                  label="Teléfono de contacto"
                  value={estudiante?.padre?.celular}
                  icon={FaPhoneAlt}
                />
              </SimpleGrid>
            </Box>

            <Box mb={6}>
              <Heading size="sm" mb={4} color="blue.600">Madre</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <DisplayField
                  label="Nombre completo"
                  value={estudiante?.madre?.nombre}
                  icon={FaUser}
                />

                <DisplayField
                  label="Número de identificación"
                  value={estudiante?.madre?.cc}
                  icon={FaIdCard}
                />

                <DisplayField
                  label="Teléfono de contacto"
                  value={estudiante?.madre?.celular}
                  icon={FaPhoneAlt}
                />
              </SimpleGrid>
            </Box>

            <Box>
              <Heading size="sm" mb={4} color="blue.600">Acudiente</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <DisplayField
                  label="Nombre completo"
                  value={estudiante?.acudiente?.nombre}
                  icon={FaUser}
                />

                <DisplayField
                  label="Número de identificación"
                  value={estudiante?.acudiente?.cc}
                  icon={FaIdCard}
                />

                <DisplayField
                  label="Teléfono de contacto"
                  value={estudiante?.acudiente?.celular}
                  icon={FaPhoneAlt}
                />
              </SimpleGrid>
            </Box>
          </TabPanel>

          {/* Información de Salud */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <DisplayField
                label="EPS"
                value={estudiante?.eps}
                icon={FaUserMd}
              />

              <DisplayField
                label="IPS"
                value={estudiante?.ips}
                icon={FaUserMd}
              />

              <DisplayField
                label="Régimen de salud"
                value={estudiante?.regimen_salud}
                icon={FaUserMd}
              />

              <DisplayField
                label="Grupo sanguíneo"
                value={estudiante?.grupo_sanguineo}
                icon={FaHeartbeat}
              />

              <DisplayField
                label="RH"
                value={estudiante?.rh}
                icon={FaHeartbeat}
              />

              <DisplayField
                label="Nivel SISBEN"
                value={estudiante?.nivel_sisben}
                icon={FaUserMd}
              />

              <DisplayField
                label="Categoría SISBEN"
                value={estudiante?.categoria_sisben}
                icon={FaUserMd}
              />

              <DisplayField
                label="Subcategoría SISBEN"
                value={estudiante?.subcategoria_sisben}
                icon={FaUserMd}
              />
            </SimpleGrid>

            {/* Capacidades y limitaciones */}
            <Box mt={6}>
              <Heading size="sm" mb={4} color="blue.600">Capacidades y limitaciones</Heading>

              {estudiante?.limitaciones && estudiante.limitaciones.length > 0 ? (
                <Box mb={4}>
                  <Text fontWeight="medium" mb={2}>Limitaciones:</Text>
                  <Flex flexWrap="wrap" gap={2}>
                    {estudiante.limitaciones.map((limitacion, index) => (
                      <Tag key={index} colorScheme="blue" borderRadius="full">
                        <TagLabel>{limitacion}</TagLabel>
                      </Tag>
                    ))}
                  </Flex>
                </Box>
              ) : (
                <Text mb={4}>No se han registrado limitaciones</Text>
              )}

              {estudiante?.capacidades_excepcionales && estudiante.capacidades_excepcionales.length > 0 ? (
                <Box mb={4}>
                  <Text fontWeight="medium" mb={2}>Capacidades excepcionales:</Text>
                  <Flex flexWrap="wrap" gap={2}>
                    {estudiante.capacidades_excepcionales.map((capacidad, index) => (
                      <Tag key={index} colorScheme="green" borderRadius="full">
                        <TagLabel>{capacidad}</TagLabel>
                      </Tag>
                    ))}
                  </Flex>
                </Box>
              ) : (
                <Text mb={4}>No se han registrado capacidades excepcionales</Text>
              )}

              <DisplayField
                label="Coeficiente intelectual"
                value={estudiante?.coeficiente_intelectual}
                icon={FaBook}
              />
            </Box>
          </TabPanel>

          {/* Información Social */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={6}>
              <DisplayField
                label="Credo religioso"
                value={estudiante?.credo_religioso}
                icon={FaChurch}
              />

              <DisplayField
                label="Denominación"
                value={estudiante?.denominacion}
                icon={FaChurch}
              />

              <DisplayField
                label="Grupo étnico"
                value={estudiante?.grupo_etnico}
                icon={FaUsers}
              />
            </SimpleGrid>

            <Box mb={6}>
              <Heading size="sm" mb={4} color="blue.600">Situación de vulnerabilidad</Heading>
              <Flex flexWrap="wrap" gap={3}>
                <BooleanTag
                  value={estudiante?.victima_conflicto}
                  label="Víctima del conflicto"
                  colorTrue="red"
                />
                <BooleanTag
                  value={estudiante?.desplazado}
                  label="Desplazado"
                  colorTrue="red"
                />
                <BooleanTag
                  value={estudiante?.desvinculado_armado}
                  label="Desvinculado de grupo armado"
                  colorTrue="red"
                />
              </Flex>
            </Box>

            {(estudiante?.victima_conflicto || estudiante?.desplazado) && (
              <Box>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <DisplayField
                    label="Departamento expulsor"
                    value={estudiante?.depto_expulsor}
                    icon={FaMapMarkerAlt}
                  />

                  <DisplayField
                    label="Municipio expulsor"
                    value={estudiante?.municipio_expulsor}
                    icon={FaMapMarkerAlt}
                  />
                </SimpleGrid>
              </Box>
            )}
          </TabPanel>

          {/* Información Adicional */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <DisplayField
                label="Turno"
                value={estudiante?.turno}
                icon={FaBook}
              />

              <DisplayField
                label="Sede"
                value={estudiante?.sede?.nombre || sedeSeleccionada?.nombre}
                icon={FaHome}
              />

              <DisplayField
                label="Fecha de registro"
                value={Moment(estudiante?.createdAt).format('LLL')}
                icon={FaUserCheck}
              />

              <DisplayField
                label="Última actualización"
                value={Moment(estudiante?.updatedAt).format('LLL')}
                icon={FaUserCheck}
              />
            </SimpleGrid>

            {estudiante?.observaciones && (
              <Box mt={6}>
                <Heading size="sm" mb={4} color="blue.600">Observaciones</Heading>
                <Box
                  p={4}
                  borderRadius="md"
                  bg={'gray.50'}
                >
                  <Text>{estudiante.observaciones}</Text>
                </Box>
              </Box>
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default DetallesEstudiante;